import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Grid, Stack, SvgIcon } from '@mui/material';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AppFormikSelect,
  AppFormikTextArea,
  AppFormikTextField,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import PageHeader from 'shared/components/PageHeader';
import { HeadTag } from 'shared/components';
import { RiskAssessmentSchema } from 'validators/RiskAssessmentSchema';
import TemplateStructureBuilder from './TemplateStructureBuilder';
import { RiskAssessmentService } from 'services/RiskAssessmentService';
import ScrollToError from 'shared/components/ScrollToError';
import { ReactComponent as Upload } from 'assets/images/ic_upload.svg';
import UploadExcelTemplate from './UploadExcelTemplate';
import { ParsedTemplate } from './excelTemplateParser';
import { Toast } from 'shared/helpers';

interface Question {
  id: string;
  text: string;
  type: string;
  options?: string[];
}

interface Subsection {
  title: string;
  questions: Question[];
}

interface Section {
  title: string;
  subsections: Subsection[];
}

interface RiskTemplateFormValues {
  name: string;
  description: string;
  applicability: string;
  sections: Section[];
}

export interface ApiPayload {
  templateName: string;
  description: string;
  scope: string;
  category: string;
  complianceMapping: string[];
  sections: Section[];
}

const initialValues: RiskTemplateFormValues = {
  name: '',
  description: '',
  applicability: 'Both',
  sections: [
    {
      title: '',
      subsections: [
        {
          title: '',
          questions: [
            {
              id: 'bias_rep_1',
              text: '',
              type: '',
              options: [],
            },
          ],
        },
      ],
    },
  ],
};

const RiskTemplateBuilder = (): ReactElement => {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const [templateDetails, setTemplateDetails] = useState<any>({});
  const [isUploadTemplate, setIsUploadTemplate] = useState(false);
  const formikRef = useRef<FormikProps<RiskTemplateFormValues>>(null);
  const isEdit = useMemo(() => !!templateId, [templateId]);
  const [isPreview, setIsPreview] = useState(isEdit);

  const transformPayload = (values: RiskTemplateFormValues): any => ({
    templateName: values.name,
    description: values.description,
    scope: values.applicability,
    complianceMapping: ['custom'],
    category: 'Data Governance & Data Quality',
    // template_structure: {
    sections: values.sections,
    // },
  });

  const handleSubmit = (
    values: RiskTemplateFormValues,
    formik: FormikHelpers<RiskTemplateFormValues>
  ): void => {
    const payload = transformPayload(values);
    if (templateId) {
      RiskAssessmentService.updateAssessmentTemplate(templateId, payload)
        .then(() => {
          console.log('Template updated successfully:', values);
          navigate('/assessment-templates');
        })
        .catch((error) => {
          console.error('Error updating template:', error);
          formik.setSubmitting(false);
        });
    } else {
      RiskAssessmentService.createAssessmentTemplate(payload)
        .then(() => {
          console.log('Template created successfully:', values);
          navigate('/assessment-templates');
        })
        .catch((error) => {
          console.error('Error creating template:', error);
          formik.setSubmitting(false);
        });
    }
  };

  // Helper to generate unique ID
  const generateUniqueId = (): string =>
    `q_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

  const handleTemplateParsed = (parsed: ParsedTemplate): void => {
    if (formikRef.current) {
      const formValues: RiskTemplateFormValues = {
        name: parsed.name,
        description: parsed.description,
        applicability: parsed.applicability,
        sections: parsed.sections.map((section) => ({
          title: section.title,
          subsections: section.subsections.map((subsection) => ({
            title: subsection.title,
            questions: subsection.questions.map((question) => ({
              id: question.id || generateUniqueId(),
              text: question.text,
              type: question.type,
              options: question.options || [],
            })),
          })),
        })),
      };

      formikRef.current.setValues(formValues);
      setIsPreview(true);
      Toast.showSuccessMessage('Template data imported successfully!');
    }
  };

  useEffect(() => {
    if (templateId) {
      RiskAssessmentService.getAssessmentTemplateDetail(templateId)
        .then((response: any) => {
          setTemplateDetails(response?.data.data || []);
          console.log(response?.data.data, 'response?.data.data');
        })
        .catch((error: any) => {
          console.error('Error fetching templates:', error);
        });
    }
  }, [templateId]);

  return (
    <>
      <PageHeader
        title={
          isEdit
            ? 'Edit Risk Assessment Template'
            : 'Create Risk Assessment Template'
        }
        actions={
          (!isEdit && (
            <AppPrimaryButton
              title="Import"
              startIcon={
                <SvgIcon
                  component={Upload}
                  inheritViewBox
                  className="view-icon"
                  fontSize="inherit"
                />
              }
              onClick={() => setIsUploadTemplate(true)}
            />
          )) ||
          undefined
        }
      />

      <Box className="paper-form-content px-8">
        <Paper variant="outlined">
          <Box className="risk-management add-new-risk mt-8">
            <HeadTag title={isEdit ? 'Edit Template' : 'Create Template'} />

            <Formik<RiskTemplateFormValues>
              innerRef={formikRef}
              initialValues={
                templateId && templateDetails
                  ? {
                      name: templateDetails?.templateName || '',
                      description: templateDetails?.description || '',
                      applicability: templateDetails?.scope || 'Both',
                      sections:
                        templateDetails?.sections || initialValues.sections,
                    }
                  : initialValues
              }
              enableReinitialize
              validationSchema={RiskAssessmentSchema}
              onSubmit={(values, formikHelpers): void => {
                handleSubmit(values, formikHelpers);
              }}
            >
              {(formik: FormikProps<RiskTemplateFormValues>) => (
                <Grid container spacing={2} className="add-register-wrapper">
                  <ScrollToError />
                  {/* Template Name */}
                  <Grid item xs={12}>
                    <AppFormikTextField
                      name="name"
                      label="Template Name*"
                      outerLabel
                      placeholder="Enter template name"
                    />
                  </Grid>

                  {/* Description */}
                  <Grid item xs={12}>
                    <AppFormikTextArea
                      name="description"
                      label="Description"
                      outerLabel
                      placeholder="Enter template description"
                      rows={2}
                    />
                  </Grid>

                  {/* Applicability */}
                  <Grid item xs={12}>
                    <AppFormikSelect
                      name="applicability"
                      label="Applicable To"
                      outerLabel
                      items={['Both', 'AI', 'Dataset']}
                      placeholder="Select"
                    />
                  </Grid>

                  {/* Template Structure Builder */}
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <TemplateStructureBuilder
                        isPreview={isPreview}
                        setIsPreview={setIsPreview}
                        isValid={formik.isValid}
                      />
                    </Paper>
                  </Grid>

                  {/* Actions */}
                  <Grid item xs={12}>
                    <Stack spacing={4} direction="row-reverse" className="mt-4">
                      <AppPrimaryButton
                        isLoading={formik.isSubmitting}
                        disabled={formik.isSubmitting}
                        title={isEdit ? 'Save Template' : 'Create Template'}
                        onClick={formik.handleSubmit}
                      />

                      <AppPrimaryButtonOutlined
                        title="Cancel"
                        disabled={formik.isSubmitting}
                        onClick={() => navigate('/assessment-templates')}
                      />
                    </Stack>
                  </Grid>
                </Grid>
              )}
            </Formik>
          </Box>
        </Paper>
      </Box>

      {isUploadTemplate && (
        <UploadExcelTemplate
          isUploadTemplate={isUploadTemplate}
          onClose={() => {
            setIsUploadTemplate(false);
          }}
          title="Upload Excel Template"
          onDataParsed={handleTemplateParsed}
        />
      )}
    </>
  );
};

export default RiskTemplateBuilder;
